import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Modal,
  ProgressBar,
  Row,
} from "react-bootstrap";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import PreviewOutlinedIcon from "@mui/icons-material/PreviewOutlined";
import styles from "../../pages/CreateEnquiryWizard.module.scss";

const TOTAL_STEPS = 4;

const DEFAULT_FORM = {
  name: "",
  targetType: "program", // 'program' | 'course'
  program_id: "",
  course_id: "",
  discount_percentage: "",
  start_date: "",
  end_date: "",
};

const STEP_META = [
  { id: 1, short: "Details", title: "Promotion details", icon: <LabelOutlinedIcon fontSize="small" /> },
  { id: 2, short: "Target", title: "Program or course", icon: <SchoolOutlinedIcon fontSize="small" /> },
  { id: 3, short: "Offer", title: "Discount & dates", icon: <LocalOfferOutlinedIcon fontSize="small" /> },
  { id: 4, short: "Review", title: "Review & create", icon: <PreviewOutlinedIcon fontSize="small" /> },
];

function PromotionWizardModal({
  show,
  onHide,
  programs = [],
  courses = [],
  onSubmit,
  submitting,
  mode = "create",
  initialData = null,
}) {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");

  const progressPercent = (currentStep / TOTAL_STEPS) * 100;

  const coursesForSelectedProgram = useMemo(() => {
    if (!formData.program_id) return courses;
    return courses.filter((c) => String(c.program_id) === String(formData.program_id));
  }, [courses, formData.program_id]);

  useEffect(() => {
    if (!show) return;
    setCurrentStep(1);
    if (mode === "edit" && initialData) {
      const isCourse = Boolean(initialData.course_id);
      const toInputDateTimeLocal = (value) => {
        if (!value) return "";
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return "";
        const tzOffset = d.getTimezoneOffset() * 60000;
        return new Date(d.getTime() - tzOffset).toISOString().slice(0, 16);
      };
      setFormData({
        name: initialData.name || "",
        targetType: isCourse ? "course" : "program",
        program_id: initialData.program_id != null ? String(initialData.program_id) : "",
        course_id: initialData.course_id != null ? String(initialData.course_id) : "",
        discount_percentage:
          initialData.discount_percentage != null ? String(initialData.discount_percentage) : "",
        start_date: toInputDateTimeLocal(initialData.start_date),
        end_date: toInputDateTimeLocal(initialData.end_date),
      });
    } else {
      setFormData(DEFAULT_FORM);
    }
    setErrors({});
    setApiError("");
  }, [show, mode, initialData]);

  const setField = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "targetType" && value === "program") next.course_id = "";
      if (field === "targetType" && value === "course") next.program_id = "";
      if (field === "program_id" && prev.targetType === "course") next.course_id = "";
      return next;
    });
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const n = { ...prev };
      delete n[field];
      return n;
    });
  };

  const validateStep1 = () => {
    const e = {};
    if (!(formData.name || "").trim()) e.name = "Name is required.";
    return e;
  };

  const validateStep2 = () => {
    const e = {};
    if (formData.targetType === "program") {
      if (!formData.program_id) e.program_id = "Select a program.";
    } else {
      if (!formData.course_id) e.course_id = "Select a course.";
    }
    return e;
  };

  const validateStep3 = () => {
    const e = {};
    const d = parseFloat(formData.discount_percentage);
    if (formData.discount_percentage === "" || Number.isNaN(d)) {
      e.discount_percentage = "Discount % is required.";
    } else if (d < 0 || d > 100) {
      e.discount_percentage = "Must be between 0 and 100.";
    }
    if (!formData.start_date) e.start_date = "Start date is required.";
    if (!formData.end_date) e.end_date = "End date is required.";
    if (formData.start_date && formData.end_date) {
      const start = new Date(formData.start_date);
      const end = new Date(formData.end_date);
      if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && end < start) {
        e.end_date = "End must be on or after start.";
      }
    }
    return e;
  };

  const handleNext = () => {
    setApiError("");
    let stepErrors = {};
    if (currentStep === 1) stepErrors = validateStep1();
    if (currentStep === 2) stepErrors = validateStep2();
    if (currentStep === 3) stepErrors = validateStep3();
    if (Object.keys(stepErrors).length) {
      setErrors((prev) => ({ ...prev, ...stepErrors }));
      return;
    }
    if (currentStep < TOTAL_STEPS) setCurrentStep((s) => s + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep((s) => s - 1);
  };

  const goToStep = (step) => {
    if (step <= currentStep) setCurrentStep(step);
  };

  const buildPayload = () => {
    const programId =
      formData.targetType === "program" && formData.program_id
        ? Number(formData.program_id)
        : null;
    const courseId =
      formData.targetType === "course" && formData.course_id ? Number(formData.course_id) : null;
    return {
      name: (formData.name || "").trim(),
      program_id: programId,
      course_id: courseId,
      discount_percentage: parseFloat(formData.discount_percentage),
      start_date: new Date(formData.start_date).toISOString(),
      end_date: new Date(formData.end_date).toISOString(),
    };
  };

  const handleCreate = async () => {
    const merged = { ...validateStep1(), ...validateStep2(), ...validateStep3() };
    if (Object.keys(merged).length) {
      setErrors(merged);
      const first = validateStep1();
      if (Object.keys(first).length) setCurrentStep(1);
      else if (Object.keys(validateStep2()).length) setCurrentStep(2);
      else setCurrentStep(3);
      return;
    }
    setApiError("");
    try {
      await onSubmit(buildPayload());
    } catch (err) {
      setApiError(err?.message || `Failed to ${mode === "edit" ? "update" : "create"} promotion.`);
    }
  };

  const renderFieldError = (field) =>
    errors[field] ? (
      <div className={styles.errorText} role="alert">
        {errors[field]}
      </div>
    ) : null;

  const selectedProgram = programs.find((p) => String(p.id) === String(formData.program_id));
  const selectedCourse = courses.find((c) => String(c.id) === String(formData.course_id));

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{mode === "edit" ? "Edit promotion" : "New promotion"}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-0">
        <div className={styles.wizardWrap} style={{ maxWidth: "100%" }}>
          <Card className={styles.wizardCard}>
            <Card.Body className={styles.cardBody}>
              <header className={styles.gwHeader}>
                <div className={styles.gwTitleSection}>
                  <h2 className={styles.gwTitle}>{mode === "edit" ? "Edit promotion" : "Create promotion"}</h2>
                  <p className={styles.gwSubtitle}>
                    Step through details, target, pricing window, then confirm.
                  </p>
                </div>
                <div className={styles.gwProgressIndicator}>
                  <div className={styles.gwProgressText}>
                    Step {currentStep} of {TOTAL_STEPS}
                  </div>
                  <ProgressBar
                    now={progressPercent}
                    className={styles.gwLinearProgress}
                    aria-label={`Progress: step ${currentStep} of ${TOTAL_STEPS}`}
                  />
                </div>
              </header>

              <nav className={styles.gwNav} aria-label="Wizard steps">
                <div className={styles.gwProgress}>
                  {STEP_META.map((step, idx) => {
                    const stepNumber = idx + 1;
                    const status =
                      stepNumber < currentStep ? "done" : stepNumber === currentStep ? "current" : "locked";
                    const stepClass =
                      status === "done"
                        ? styles.gwStepDone
                        : status === "current"
                          ? styles.gwStepCurrent
                          : styles.gwStepLocked;
                    const badgeClass =
                      status === "done"
                        ? styles.gwStepBadgeDone
                        : status === "current"
                          ? styles.gwStepBadgeCurrent
                          : styles.gwStepBadgeLocked;
                    const shortClass =
                      status === "done"
                        ? styles.gwStepShortDone
                        : status === "current"
                          ? styles.gwStepShortCurrent
                          : styles.gwStepShortLocked;

                    return (
                      <button
                        key={step.id}
                        type="button"
                        className={`${styles.gwStep} ${stepClass}`}
                        onClick={() => goToStep(stepNumber)}
                        disabled={stepNumber > currentStep}
                        aria-label={`Go to step ${stepNumber}: ${step.title}`}
                      >
                        <div className={`${styles.gwStepBadge} ${badgeClass}`}>
                          {status === "done" ? <CheckCircleRoundedIcon fontSize="small" /> : step.icon}
                        </div>
                        <div className={styles.gwStepLabel}>
                          <div className={`${styles.gwStepShort} ${shortClass}`}>{step.short}</div>
                          <div className={styles.gwStepTitle}>{step.title}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </nav>

              {apiError ? <Alert variant="danger">{apiError}</Alert> : null}

              <Form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (currentStep === TOTAL_STEPS) handleCreate();
                  else handleNext();
                }}
                noValidate
              >
                <section className={styles.stepBody} aria-live="polite">
                  {currentStep === 1 && (
                    <Row className="g-3">
                      <Col md={12}>
                        <Form.Group controlId="promoName">
                          <Form.Label>Promotion name *</Form.Label>
                          <Form.Control
                            type="text"
                            value={formData.name}
                            onChange={(e) => setField("name", e.target.value)}
                            isInvalid={Boolean(errors.name)}
                            placeholder="e.g. Spring intake discount"
                            autoFocus
                          />
                          {renderFieldError("name")}
                        </Form.Group>
                      </Col>
                    </Row>
                  )}

                  {currentStep === 2 && (
                    <Row className="g-3">
                      <Col md={12}>
                        <Form.Label className="d-block">Apply to *</Form.Label>
                        <div className="d-flex flex-wrap gap-3 mb-3">
                          <Form.Check
                            type="radio"
                            id="target-program"
                            name="targetType"
                            label="Whole program"
                            checked={formData.targetType === "program"}
                            onChange={() => setField("targetType", "program")}
                          />
                          <Form.Check
                            type="radio"
                            id="target-course"
                            name="targetType"
                            label="Single course (overrides program promotion)"
                            checked={formData.targetType === "course"}
                            onChange={() => setField("targetType", "course")}
                          />
                        </div>
                      </Col>
                      {formData.targetType === "program" ? (
                        <Col md={12}>
                          <Form.Group controlId="promoProgram">
                            <Form.Label>Program *</Form.Label>
                            <Form.Select
                              value={formData.program_id}
                              onChange={(e) => setField("program_id", e.target.value)}
                              isInvalid={Boolean(errors.program_id)}
                            >
                              <option value="">Select program</option>
                              {programs.map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.name}
                                </option>
                              ))}
                            </Form.Select>
                            {renderFieldError("program_id")}
                          </Form.Group>
                        </Col>
                      ) : (
                        <>
                          <Col md={12}>
                            <Form.Group controlId="promoProgramFilter">
                              <Form.Label>Filter by program (optional)</Form.Label>
                              <Form.Select
                                value={formData.program_id}
                                onChange={(e) => setField("program_id", e.target.value)}
                              >
                                <option value="">All programs</option>
                                {programs.map((p) => (
                                  <option key={p.id} value={p.id}>
                                    {p.name}
                                  </option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={12}>
                            <Form.Group controlId="promoCourse">
                              <Form.Label>Course *</Form.Label>
                              <Form.Select
                                value={formData.course_id}
                                onChange={(e) => setField("course_id", e.target.value)}
                                isInvalid={Boolean(errors.course_id)}
                              >
                                <option value="">Select course</option>
                                {coursesForSelectedProgram.map((c) => (
                                  <option key={c.id} value={c.id}>
                                    {c.name}
                                  </option>
                                ))}
                              </Form.Select>
                              {renderFieldError("course_id")}
                            </Form.Group>
                          </Col>
                        </>
                      )}
                    </Row>
                  )}

                  {currentStep === 3 && (
                    <Row className="g-3">
                      <Col md={4}>
                        <Form.Group controlId="promoDiscount">
                          <Form.Label>Discount % *</Form.Label>
                          <Form.Control
                            type="number"
                            min={0}
                            max={100}
                            step={0.01}
                            value={formData.discount_percentage}
                            onChange={(e) => setField("discount_percentage", e.target.value)}
                            isInvalid={Boolean(errors.discount_percentage)}
                          />
                          {renderFieldError("discount_percentage")}
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group controlId="promoStart">
                          <Form.Label>Start *</Form.Label>
                          <Form.Control
                            type="datetime-local"
                            value={formData.start_date}
                            onChange={(e) => setField("start_date", e.target.value)}
                            isInvalid={Boolean(errors.start_date)}
                          />
                          {renderFieldError("start_date")}
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group controlId="promoEnd">
                          <Form.Label>End *</Form.Label>
                          <Form.Control
                            type="datetime-local"
                            value={formData.end_date}
                            onChange={(e) => setField("end_date", e.target.value)}
                            isInvalid={Boolean(errors.end_date)}
                          />
                          {renderFieldError("end_date")}
                        </Form.Group>
                      </Col>
                    </Row>
                  )}

                  {currentStep === 4 && (
                    <div className="small">
                      <p>
                        <strong>Name:</strong> {(formData.name || "").trim() || "—"}
                      </p>
                      <p>
                        <strong>Target:</strong>{" "}
                        {formData.targetType === "program"
                          ? selectedProgram?.name || "—"
                          : `Course: ${selectedCourse?.name || "—"}`}
                      </p>
                      <p>
                        <strong>Discount:</strong> {formData.discount_percentage || "—"}%
                      </p>
                      <p>
                        <strong>Window:</strong> {formData.start_date || "—"} → {formData.end_date || "—"}
                      </p>
                      <p className="text-muted mb-0">
                        {mode === "edit"
                          ? "Save changes to update this promotion."
                          : "The promotion will be created as "}
                        {mode === "edit" ? null : <strong>draft</strong>}
                        {mode === "edit" ? null : ". Submit it from the list for approval."}
                      </p>
                    </div>
                  )}
                </section>

                <div className={styles.actions}>
                  {currentStep > 1 ? (
                    <Button type="button" variant="outline-secondary" onClick={handleBack} disabled={submitting}>
                      Back
                    </Button>
                  ) : (
                    <Button type="button" variant="outline-secondary" onClick={onHide} disabled={submitting}>
                      Cancel
                    </Button>
                  )}
                  {currentStep < TOTAL_STEPS ? (
                    <Button type="submit" variant="primary">
                      Next
                    </Button>
                  ) : (
                    <Button type="submit" variant="primary" className={styles.submitBtn} disabled={submitting}>
                      {submitting
                        ? mode === "edit"
                          ? "Saving…"
                          : "Creating…"
                        : mode === "edit"
                          ? "Save changes"
                          : "Create promotion"}
                    </Button>
                  )}
                </div>
              </Form>
            </Card.Body>
          </Card>
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default PromotionWizardModal;
